// 语音体系（作者 2026-11-02：“语音体系规划下，什么场景下报告什么语音，都要有设定”）。
// 这里是规则表 + 调度闸门，纯数据与纯函数，Node 可直接测。场景说明与理由见 docs/语音体系.md（两边要一致）。
//
// 分工（作者 2026-11-02 对齐）：
//   narrator 开场：新一轮第一副开局，播报员说一句开场白；这几秒角色不说话。
//   call     播报：出牌、不出、牌型、炸弹，以及牌局事实（上游、报牌、剩两张、借风、进贡、还牌、抗贡、升级、过 A）。
//            全部由播报员（与开场同一个声音）说，排队播、不互相打断；桌面上不再写“不出”和牌型名。
//   chat     口头禅：只有等待时——轮到你太久时牌友催促、等别人出牌时闲聊；用角色自己的声音，带文字气泡。有概率与冷却，不抢话。

export type Channel = 'narrator' | 'call' | 'chat';

export interface VoiceRule {
  scene: string; // 什么场景（给人看的，文档同名）
  channel: Channel;
  chance: number; // 触发后真的说的概率
  cooldownMs: number; // 同一角色同一事件的冷却
}

const R = (scene: string, channel: Channel, chance = 1, cooldownMs = 0): VoiceRule => ({ scene, channel, chance, cooldownMs });

export const VOICE_RULES: Record<string, VoiceRule> = {
  opening: R('新一轮第一副开局（开场白）', 'narrator'),
  play: R('出牌：单张报点数、对子报“对几”、其他报牌型', 'call'),
  pass: R('不出', 'call'),
  first: R('有人拿到上游', 'call'),
  report: R('有人剩 10 张以内（报牌）', 'call'),
  lastCards: R('有人只剩两张', 'call'),
  borrowWind: R('接风（借风）', 'call'),
  tribute: R('第 2 副起开局进贡', 'call'),
  returnCard: R('进贡后还牌', 'call'),
  resist: R('下游方有两个大王抗贡', 'call'),
  roundUs: R('一副结束、我方升级', 'call'),
  roundThem: R('一副结束、对方升级', 'call'),
  matchWin: R('整场我方过 A', 'call'),
  matchLose: R('整场对方过 A', 'call'),
  urge: R('轮到你、超过 10 秒没出（牌友催）', 'chat', 1, 20000),
  idle: R('等别人出牌时（机器人轮次开始，牌友闲聊）', 'chat', 0.3, 30000),
};

/** 出牌播报事件（play_single_7、play_pair_14、play_straight…）都归到 play 规则。 */
export const ruleOf = (ev: string): VoiceRule | undefined => VOICE_RULES[ev.startsWith('play_') ? 'play' : ev];

/**
 * 口头禅不抢话：上一句口头禅或开场白之后至少空这么久；两句口头禅之间至少空这么久。
 * 播报员报牌不算（作者 11-02 复现“一会儿有一会儿没有”：报牌每 0.65 秒一句，算进去闲聊永远等不到空档）；
 * 播报和口头禅不重叠由播放器排队保证（voice.ts：口头禅等播报说完，播报也等口头禅说完）。
 */
export const CHAT_QUIET_MS = 2500;
export const CHAT_GAP_MS = 15000;
/** 催促：轮到你后多久第一次催、之后多久再催、一手最多催几次。 */
export const URGE_AFTER_MS = 10000;
export const URGE_AGAIN_MS = 15000;
export const URGE_MAX = 2;

/** 调度闸门：决定这一句说不说。now 用毫秒，rand 传 0–1 随机数（测试可固定）。 */
export class VoiceGate {
  private lastAny = -1e12;
  private lastChat = -1e12;
  private lastBy = new Map<string, number>();
  muteChars = false; // 新一轮第一副开场白期间：角色闭嘴

  allow(who: string, ev: string, now: number, rand: number): boolean {
    const rule = ruleOf(ev);
    if (!rule) return false;
    if (rule.channel === 'chat' && this.muteChars) return false; // 开场白时角色不说话，播报照常
    if (rule.channel === 'chat') {
      if (now - this.lastAny < CHAT_QUIET_MS || now - this.lastChat < CHAT_GAP_MS) return false;
      if (now - (this.lastBy.get(`${who}:${ev}`) ?? -1e12) < rule.cooldownMs) return false;
    }
    if (rand >= rule.chance) return false;
    if (rule.channel !== 'call') this.lastAny = now; // 报牌不占“不抢话”的空档
    if (rule.channel === 'chat') this.lastChat = now;
    this.lastBy.set(`${who}:${ev}`, now);
    return true;
  }

  reset(): void {
    this.lastAny = this.lastChat = -1e12;
    this.lastBy.clear();
    this.muteChars = false;
  }
}
